import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API, errDetail, fmtEur, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Correspondence } from "../components/Correspondence";
import { EightDReport } from "../components/quality/EightDReport";
import { Ishikawa } from "../components/quality/Ishikawa";
import { CapaList } from "../components/quality/CapaList";
import { D_STEPS } from "../lib/eightd";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { ClipboardCheck, Plus, AlertTriangle, ShieldAlert, CheckCircle2, Upload, Paperclip, MessageSquareReply } from "lucide-react";

const STATUS = {
  open: ["bg-gray-100 text-gray-700", "Offen"],
  in_8d: ["bg-amber-100 text-amber-800", "8D läuft"],
  effectiveness_pending: ["bg-blue-100 text-blue-700", "Wirksamkeit geprüft"],
  closed: ["bg-green-100 text-green-700", "Geschlossen"],
  rejected: ["bg-red-100 text-red-700", "Abgelehnt"],
};
const SEV = { Minor: "bg-gray-100 text-gray-600", Major: "bg-amber-100 text-amber-800", Critical: "bg-red-100 text-red-700" };
const St = ({ s }) => { const [c, l] = STATUS[s] || ["bg-gray-100", s]; return <Badge className={c}>{l}</Badge>; };

export default function SupplierComplaints() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [caps, setCaps] = useState([]);
  const [cfg, setCfg] = useState({ defect_categories: [], severities: [], d_steps: [], copq_buckets: [] });
  const [copq, setCopq] = useState(null);
  const [detail, setDetail] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [recOnly, setRecOnly] = useState(false);
  const load = useCallback(() => {
    api.get(`/srm/complaints${recOnly ? "?recurrence=true" : ""}`).then((r) => setRows(r.data || [])).catch((e) => toast.error(errDetail(e)));
    api.get("/srm/quality/copq-summary").then((r) => setCopq(r.data)).catch(() => {});
  }, [recOnly]);
  useEffect(() => { api.get("/srm/my-caps").then((r) => setCaps(r.data.caps || [])).catch(() => {}); api.get("/srm/quality/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  // Deep-link: /supplier-complaints?id=<complaintId> opens that complaint directly
  const deepId = searchParams.get("id");
  useEffect(() => { if (deepId) setDetail(deepId); }, [deepId]);
  const closeDetail = (ch) => {
    setDetail(null);
    if (searchParams.get("id")) { searchParams.delete("id"); setSearchParams(searchParams, { replace: true }); }
    if (ch) load();
  };
  const canManage = caps.includes("complaint_manage") || caps.includes("srm_admin");

  return (
    <div className="p-6 max-w-[1300px] mx-auto" data-testid="complaints-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center"><ClipboardCheck className="h-5 w-5 text-[#F5A623]" /></div>
          <div><h1 className="text-2xl font-bold text-[#1B2A4A]">Lieferanten-Reklamationen (8D)</h1><p className="text-sm text-gray-500">8D · COPQ · Wiederholfehler</p></div>
        </div>
        {canManage && <Button data-testid="complaint-new-btn" className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={() => setAddOpen(true)}><Plus className="h-4 w-4 mr-1" /> Neue Reklamation</Button>}
      </div>

      {copq && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="copq-total"><div className="text-xs text-gray-500">COPQ gesamt</div><div className="text-xl font-bold text-[#1B2A4A]">{fmtEur(copq.total)}</div></div>
          {cfg.copq_buckets.map((b) => <div key={b} className="bg-white rounded-xl border border-gray-200 p-4"><div className="text-xs text-gray-500 capitalize">{b}</div><div className="text-lg font-semibold text-gray-700">{fmtEur(copq.buckets[b])}</div></div>)}
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <button data-testid="filter-recurrence" onClick={() => setRecOnly((v) => !v)} className={`text-sm px-3 py-1.5 rounded-full border ${recOnly ? "bg-red-50 border-red-300 text-red-700" : "border-gray-200 text-gray-600"}`}>
          <AlertTriangle className="h-3.5 w-3.5 inline mr-1" /> Nur Wiederholfehler
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Nummer</TableHead><TableHead>Lieferant</TableHead><TableHead>Werk</TableHead><TableHead>Defekt</TableHead><TableHead>Schwere</TableHead><TableHead>COPQ</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">Keine Reklamationen</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} className="cursor-pointer hover:bg-gray-50" data-testid={`complaint-row-${r.complaint_number}`} onClick={() => setDetail(r.id)}>
                <TableCell className="font-mono font-medium text-[#1B2A4A]">{r.complaint_number}{r.recurrence?.is_recurrence && <Badge className="ml-2 bg-red-100 text-red-700" data-testid="recurrence-badge">Wiederholung</Badge>}{r.stock_blocked && <ShieldAlert className="h-4 w-4 inline ml-1 text-red-600" title="Bestand gesperrt" />}</TableCell>
                <TableCell>{r.supplier_name}</TableCell><TableCell>{r.plant_name || "–"}</TableCell>
                <TableCell>{r.defect_category || "–"}</TableCell>
                <TableCell><Badge className={SEV[r.severity]}>{r.severity}</Badge></TableCell>
                <TableCell>{fmtEur(r.copq_total)}</TableCell><TableCell><St s={r.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {addOpen && <NewComplaintDialog cfg={cfg} onClose={(ch) => { setAddOpen(false); if (ch) load(); }} />}
      {detail && <ComplaintDetail id={detail} cfg={cfg} canManage={canManage} onClose={closeDetail} />}
    </div>
  );
}

function NewComplaintDialog({ cfg, onClose }) {
  const [suppliers, setSuppliers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [form, setForm] = useState({ supplier_id: "", plant_id: "", defect_category: "", severity: "Major", qty_affected: 0, description: "", stock_blocked: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/suppliers?status=approved").then((r) => setSuppliers(r.data || [])).catch(() => {});
    api.get("/plants").then((r) => setPlants(r.data || [])).catch(() => {});
  }, []);
  const save = async () => {
    if (!form.supplier_id || !form.defect_category) { toast.error("Lieferant und Defektkategorie erforderlich"); return; }
    setBusy(true);
    try { await api.post("/srm/complaints", form); toast.success("Reklamation angelegt"); onClose(true); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-lg" data-testid="complaint-new-dialog">
        <DialogHeader><DialogTitle>Neue Lieferanten-Reklamation</DialogTitle><DialogDescription>Wiederholfehler werden automatisch (24 Mon., werkübergreifend) erkannt.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Lieferant *</Label>
            <Select value={form.supplier_id} onValueChange={(v) => setForm((f) => ({ ...f, supplier_id: v }))}>
              <SelectTrigger data-testid="complaint-supplier"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Werk</Label>
              <Select value={form.plant_id} onValueChange={(v) => setForm((f) => ({ ...f, plant_id: v }))}>
                <SelectTrigger data-testid="complaint-plant"><SelectValue placeholder="Wählen…" /></SelectTrigger>
                <SelectContent>{plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Menge betroffen</Label><Input type="number" data-testid="complaint-qty" value={form.qty_affected} onChange={(e) => setForm((f) => ({ ...f, qty_affected: e.target.value }))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Defektkategorie *</Label>
              <Select value={form.defect_category} onValueChange={(v) => setForm((f) => ({ ...f, defect_category: v }))}>
                <SelectTrigger data-testid="complaint-category"><SelectValue placeholder="Wählen…" /></SelectTrigger>
                <SelectContent>{cfg.defect_categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Schwere</Label>
              <Select value={form.severity} onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}>
                <SelectTrigger data-testid="complaint-severity"><SelectValue /></SelectTrigger>
                <SelectContent>{cfg.severities.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label>Beschreibung</Label><Textarea data-testid="complaint-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
          <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="complaint-stockblock" checked={form.stock_blocked} onCheckedChange={(v) => setForm((f) => ({ ...f, stock_blocked: !!v }))} /> Bestand sperren</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onClose(false)}>Abbrechen</Button>
          <Button data-testid="complaint-save-btn" disabled={busy} className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={save}>Anlegen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ComplaintDetail({ id, cfg, canManage, onClose }) {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [effNote, setEffNote] = useState("");
  const [files, setFiles] = useState([]);
  const [owners, setOwners] = useState([]);
  const [savingKey, setSavingKey] = useState(null);
  const fileRef = useRef(null);
  const load = useCallback(() => { api.get(`/srm/complaints/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(errDetail(e))); }, [id]);
  const loadFiles = useCallback(() => { api.get(`/srm/complaints/${id}/files`).then((r) => setFiles(r.data || [])).catch(() => {}); }, [id]);
  useEffect(() => { load(); loadFiles(); }, [load, loadFiles]);
  useEffect(() => { api.get("/users").then((r) => setOwners((r.data || []).map((u) => ({ id: u.id, name: u.name })))).catch(() => {}); }, []);
  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/srm/complaints/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Datei hochgeladen"); loadFiles(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); } };
  const onChangeStep = (key, patch) => setC((prev) => ({ ...prev, eight_d: { ...prev.eight_d, [key]: { ...(prev.eight_d[key] || {}), ...patch } } }));
  const onSaveStep = async (key) => {
    const s = c.eight_d[key] || {};
    setSavingKey(key);
    try {
      const r = await api.put(`/srm/complaints/${id}/8d/${key}`, {
        content: s.content || "", done: !!s.done, owner_id: s.owner_id || null,
        owner_name: s.owner_name || null, due: s.due || null, five_why: s.five_why || "",
      });
      setC((prev) => ({ ...prev, eight_d: r.data.eight_d, status: r.data.status }));
      toast.success("8D gespeichert");
    } catch (e) { toast.error(errDetail(e)); } finally { setSavingKey(null); }
  };
  const saveIshikawa = (v) => { setC((prev) => ({ ...prev, ishikawa: v })); };
  const commitIshikawa = () => act(() => api.put(`/srm/complaints/${id}/ishikawa`, c.ishikawa || {}), "Ishikawa gespeichert");
  const saveCapaLocal = (v) => { setC((prev) => ({ ...prev, capa: v })); };
  const commitCapa = () => act(() => api.put(`/srm/complaints/${id}/capa`, { capa: c.capa || [] }), "CAPA gespeichert");
  const saveCopq = (patch) => act(() => api.put(`/srm/complaints/${id}/copq`, patch), "COPQ gespeichert");
  const verify = () => act(() => api.post(`/srm/complaints/${id}/verify-effectiveness`, { note: effNote }), "Wirksamkeit bestätigt");
  const close = async () => { setBusy(true); try { await api.post(`/srm/complaints/${id}/close`); toast.success("Geschlossen"); onClose(true); } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); } };
  if (!c) return null;
  const closed = c.status === "closed";
  const token = localStorage.getItem("lf_token");
  const sr = c.supplier_response || {};
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-3xl" data-testid="complaint-detail-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-mono">{c.complaint_number} <St s={c.status} /> <Badge className={SEV[c.severity]}>{c.severity}</Badge>
          {c.recurrence?.is_recurrence && <Badge className="bg-red-100 text-red-700">Wiederholfehler ({c.recurrence.prior_count})</Badge>}</DialogTitle>
          <DialogDescription>{c.supplier_name} · {c.plant_name} · {c.defect_category} · {c.article_name || "–"} · Charge {c.batch || "–"}</DialogDescription></DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          {c.source_customer_complaint_number && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800" data-testid="source-customer-complaint">
              Aus Kundenreklamation <b className="font-mono">{c.source_customer_complaint_number}</b>
              {c.source_customer_complaint_id && <a href={`/complaints/${c.source_customer_complaint_id}`} className="underline ml-1">öffnen</a>}
            </div>
          )}
          {c.recurrence?.is_recurrence && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="recurrence-warning">Wiederholfehler: {c.recurrence.prior_count} vorheriger Vorfall in 24 Mon. ({(c.recurrence.prior_numbers || []).join(", ")}).</div>}

          {(sr.d3 || sr.d4 || sr.d5) && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm" data-testid="supplier-response">
              <div className="font-semibold text-emerald-800 flex items-center gap-1.5 mb-1"><MessageSquareReply className="h-3.5 w-3.5" />Lieferanten-Rückmeldung (Portal)</div>
              {sr.d3 && <p><b>D3:</b> {sr.d3}</p>}{sr.d4 && <p><b>D4:</b> {sr.d4}</p>}{sr.d5 && <p><b>D5:</b> {sr.d5}</p>}
              <p className="text-xs text-emerald-700 mt-1">— {sr.updated_by}</p>
            </div>
          )}

          <div><h4 className="text-sm font-semibold text-[#1B2A4A] mb-2">COPQ (Fehlerkosten)</h4>
            <div className="grid grid-cols-4 gap-2">
              {cfg.copq_buckets.map((b) => (
                <div key={b}><Label className="text-xs capitalize">{b}</Label>
                  <Input type="number" data-testid={`copq-${b}`} defaultValue={c.copq[b] || 0} disabled={closed || !canManage}
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== (c.copq[b] || 0)) saveCopq({ [b]: v }); }} /></div>
              ))}
            </div>
            <div className="text-right text-sm text-gray-600 mt-1">Summe: <b>{fmtEur(c.copq_total)}</b></div>
          </div>

          <div><EightDReport value={c.eight_d || {}} steps={D_STEPS} disabled={closed || !canManage}
            owners={owners} onChangeStep={onChangeStep} onSaveStep={onSaveStep} savingKey={savingKey} /></div>

          <div className="border-t pt-3">
            <Ishikawa value={c.ishikawa || {}} onChange={saveIshikawa} disabled={closed || !canManage} />
            {canManage && !closed && <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={commitIshikawa} disabled={busy} data-testid="sc-ishikawa-save">Ishikawa speichern</Button>}
          </div>

          <div className="border-t pt-3">
            <CapaList items={c.capa || []} onChange={saveCapaLocal} owners={owners} disabled={closed || !canManage} />
            {canManage && !closed && <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={commitCapa} disabled={busy} data-testid="sc-capa-save">CAPA speichern</Button>}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-[#1B2A4A] flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" />Bilder & Dokumente</h4>
              {canManage && !closed && (<>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
                  onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} />
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => fileRef.current?.click()} data-testid="sc-upload-btn"><Upload className="h-3 w-3 mr-1" />Hochladen</Button>
              </>)}
            </div>
            {files.length === 0 && <p className="text-xs text-gray-400">–</p>}
            <div className="grid grid-cols-4 gap-2" data-testid="sc-files">
              {files.map((f) => {
                const url = `${API}/files/download/${f.id}?auth=${token}`;
                return f.content_type?.startsWith("image/") ? (
                  <a key={f.id} href={url} target="_blank" rel="noreferrer"><img src={url} alt={f.original_filename} className="rounded-md border border-gray-200 object-cover h-20 w-full" /></a>
                ) : (
                  <a key={f.id} href={url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 underline flex items-center justify-center text-center h-20 border rounded-md px-1 break-all">{f.original_filename}</a>
                );
              })}
            </div>
          </div>

          {c.effectiveness?.verified ? (
            <div data-testid="effectiveness-panel" className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800"><CheckCircle2 className="h-4 w-4 inline mr-1" /> Wirksamkeit bestätigt von {c.effectiveness.verified_by}: {c.effectiveness.note}</div>
          ) : canManage && !closed && (
            <div data-testid="effectiveness-panel" className="border-t pt-3 space-y-2"><Label>Wirksamkeit verifizieren (Pflicht vor Abschluss)</Label>
              <Textarea data-testid="eff-note-input" value={effNote} onChange={(e) => setEffNote(e.target.value)} placeholder="Nachweis der Wirksamkeit…" />
              <Button size="sm" data-testid="eff-verify-btn" disabled={busy || !effNote} onClick={verify}>Wirksamkeit bestätigen</Button></div>
          )}

          <div className="border-t pt-3"><Correspondence kind="supplier" id={id} canWrite={canManage} /></div>
        </div>
        <DialogFooter>
          {canManage && !closed && c.effectiveness?.verified && <Button data-testid="complaint-close-btn" disabled={busy} className="bg-green-600 hover:bg-green-700" onClick={close}>Reklamation schließen</Button>}
          <Button variant="outline" onClick={() => onClose(false)}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
